import type { SaveError } from '@/hooks/useUser'

interface SyncStatusBannerProps {
  saveError: SaveError | null
  offlineSince: number | null
  onRetry: () => void
  onDismiss: () => void
}

function messageForSaveError(err: SaveError): { title: string; body: string } {
  switch (err.kind) {
    case 'encryption':
      return {
        title: 'Encryption failed — drinks not saved',
        body: 'The encryption key is missing or broken. Your recent drinks are only in this browser. Unlock with your password and retry, or copy your data out via Settings → Download All Data before reloading.',
      }
    case 'network':
      return {
        title: 'Sync failed — you appear to be offline',
        body: 'Drinks you add right now will not reach the server. When you\'re back online, hit Retry before closing the tab.',
      }
    case 'blocked':
      return {
        title: 'Save blocked — refusing to overwrite existing data',
        body: 'A safety check stopped a blank save. This usually means something cleared your session. Do not refresh — open Settings → Download All Data to back up first.',
      }
    case 'http': {
      const status = err.status ? ` (${err.status})` : ''
      return {
        title: `Sync failed — server rejected the save${status}`,
        body: err.message || 'The server refused the save. Your recent changes are only in this browser.',
      }
    }
    default:
      return {
        title: 'Sync failed',
        body: err.message || 'Something went wrong saving. Retry, or back up via Settings → Download All Data.',
      }
  }
}

export default function SyncStatusBanner({ saveError, offlineSince, onRetry, onDismiss }: SyncStatusBannerProps) {
  if (saveError) {
    const { title, body } = messageForSaveError(saveError)
    const ageMinutes = Math.floor((Date.now() - saveError.at) / 60000)
    return (
      <div
        role="alert"
        aria-live="assertive"
        className="border border-[var(--bac-danger)] bg-[var(--bac-danger)]/10 text-[var(--text-primary)] rounded-lg px-4 py-3 flex flex-col gap-2"
      >
        <div className="flex items-start gap-3">
          <svg className="w-5 h-5 text-[var(--bac-danger)] flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01M4.93 19h14.14c1.54 0 2.5-1.67 1.73-3L13.73 4a2 2 0 00-3.46 0L3.2 16c-.77 1.33.19 3 1.73 3z" />
          </svg>
          <div className="flex-1 min-w-0">
            <p className="font-semibold">{title}</p>
            <p className="text-sm text-[var(--text-secondary)] mt-1">{body}</p>
            <p className="text-xs text-[var(--text-secondary)] mt-1">
              Last attempt: {ageMinutes === 0 ? 'just now' : `${ageMinutes} min ago`}
            </p>
          </div>
        </div>
        <div className="flex gap-2 justify-end">
          <button
            type="button"
            onClick={onDismiss}
            className="px-3 py-1 text-sm rounded bg-[var(--bg-card)] text-[var(--text-secondary)] hover:bg-[var(--bg-secondary)] transition-colors"
          >
            Dismiss
          </button>
          <button
            type="button"
            onClick={onRetry}
            className="px-3 py-1 text-sm rounded bg-[var(--bac-danger)] text-white hover:opacity-90 transition-opacity"
          >
            Retry save
          </button>
        </div>
      </div>
    )
  }

  if (offlineSince != null) {
    const ageMinutes = Math.floor((Date.now() - offlineSince) / 60000)
    return (
      <div
        role="status"
        aria-live="polite"
        className="border border-[var(--accent-orange)] bg-[var(--accent-orange)]/10 text-[var(--text-primary)] rounded-lg px-4 py-3 flex items-start gap-3"
      >
        <svg className="w-5 h-5 text-[var(--accent-orange)] flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18.364 5.636a9 9 0 010 12.728m0 0l-2.829-2.829m2.829 2.829L21 21M15.536 8.464a5 5 0 010 7.072m0 0l-2.829-2.829m-4.243 2.829a4.978 4.978 0 01-1.414-2.83m-1.414 5.658a9 9 0 01-2.167-9.238m7.824 2.167a1 1 0 111.414 1.414m-1.414-1.414L3 3m8.293 8.293l1.414 1.414" />
        </svg>
        <div className="flex-1 min-w-0">
          <p className="font-semibold">Offline — showing cached data</p>
          <p className="text-sm text-[var(--text-secondary)] mt-1">
            Server unreachable. Cached snapshot is {ageMinutes === 0 ? 'less than a minute' : `${ageMinutes} min`} old. New drinks will not sync until you reconnect.
          </p>
        </div>
      </div>
    )
  }

  return null
}
